import React, { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import {
  extend,
  extend as applyThree,
  useFrame,
  useThree,
} from "@react-three/fiber";
import { apply as applySpring, a, useSpring } from "react-spring/three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass";
import { GlitchPass } from "./postprocessing/GlitchPass";

extend({ EffectComposer, RenderPass });
applySpring({ GlitchPass });
applyThree({ GlitchPass });

/**
 * This renders text via canvas and projects it as a sprite
 * @param children
 * @param position
 * @param opacity
 * @param color
 * @param fontSize
 * @returns {*}
 * @constructor
 */
function Text({
  children,
  position,
  opacity,
  color = "white",
  fontSize = 100,
}) {
  const {
    size: { width, height },
    viewport: { width: viewportWidth, height: viewportHeight },
  } = useThree();
  const scale = viewportWidth > viewportHeight ? viewportWidth : viewportHeight;
  const canvas = useMemo(() => {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 2048;
    const context = canvas.getContext("2d");
    context.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, avenir next, avenir, helvetica neue, helvetica, ubuntu, roboto, noto, segoe ui, arial, sans-serif`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillStyle = color;
    context.fillText(children, 1024, 1024 - 410 / 2);
    return canvas;
  }, [children, width, height, color, fontSize]);
  return (
    <a.sprite scale={[scale, scale, 1]} position={position}>
      <a.spriteMaterial attach="material" transparent opacity={opacity}>
        <canvasTexture
          attach="map"
          image={canvas}
          premultiplyAlpha
          onUpdate={(s) => (s.needsUpdate = true)}
        />
      </a.spriteMaterial>
    </a.sprite>
  );
}

/**
 * This component creates a fullscreen colored plane
 * @param color
 * @returns {*}
 * @constructor
 */
function Background({ color }) {
  const { viewport } = useThree();
  return (
    <mesh scale={[viewport.width, viewport.height, 1]}>
      <planeGeometry attach="geometry" args={[1, 1]} />
      <a.meshBasicMaterial attach="material" color={color} depthTest={false} />
    </mesh>
  );
}

/**
 * This component rotates a bunch of stars
 * @param position
 * @returns {*}
 * @constructor
 */
function Stars({ position }) {
  let group = useRef();
  let theta = 0;
  useFrame(() => {
    const r = 5 * Math.sin(THREE.Math.degToRad((theta += 0.01)));
    const s = Math.cos(THREE.Math.degToRad(theta * 2));
    group.current.rotation.set(r, r, r);
    group.current.scale.set(s, s, s);
  });
  const [geo, mat, coords] = useMemo(() => {
    const geo = new THREE.SphereBufferGeometry(1, 10, 10);
    const mat = new THREE.MeshBasicMaterial({
      color: new THREE.Color("peachpuff"),
      transparent: true,
    });
    const coords = new Array(1000)
      .fill()
      .map((i) => [
        Math.random() * 800 - 400,
        Math.random() * 800 - 400,
        Math.random() * 800 - 400,
      ]);
    return [geo, mat, coords];
  }, []);
  return (
    <a.group ref={group} position={position}>
      {coords.map(([p1, p2, p3], i) => (
        <mesh key={i} geometry={geo} material={mat} position={[p1, p2, p3]} />
      ))}
    </a.group>
  );
}

/**
 * This component creates a glitch effect
 * @type {{compare: (null|*), $$typeof: number, type: *}}
 */
const Effects = React.memo(({ factor }) => {
  const { gl, scene, camera, size } = useThree();
  const composer = useRef();
  useEffect(
    () => void composer.current.setSize(size.width, size.height),
    [size]
  );
  // This takes over as the main render-loop (when 2nd arg is set to true)
  useFrame(() => composer.current.render(), 1);
  return (
    <effectComposer ref={composer} args={[gl]}>
      <renderPass attachArray="passes" args={[scene, camera]} />
      <a.glitchPass attachArray="passes" renderToScreen factor={factor} />
    </effectComposer>
  );
});

/**
 * Make top background for homepage
 * @returns {*}
 * @constructor
 */
const TopBackground = () => {
  const { size } = useThree();
  const scrollMax = size.height * 4.5;
  const [{ top, mouse }, set] = useSpring(() => ({ top: 0, mouse: [0, 0] }));
  return (
    <>
      <a.spotLight
        intensity={1.2}
        color="white"
        position={mouse.interpolate((x, y) => [x / 100, -y / 100, 6.5])}
      />
      {/*<Effects factor={top.interpolate([0, 150], [1, 0])} />*/}
      <Effects factor={top.interpolate([0, 1.5], [1, 0])} />
      <Background
        color={top.interpolate(
          [0, scrollMax * 0.25, scrollMax * 0.8, scrollMax],
          ["#020c1b", "#247BA0", "#70C1B3", "#f8f3f1"]
        )}
      />
      <Stars position={top.interpolate((top) => [0, -1 + top / 20, 0])} />
      <Text
        opacity={top.interpolate([0, 400], [1, 0])}
        position={top.interpolate((top) => [0, -1 + top / 200, 0])}
      >
        quan.le
      </Text>
    </>
  );
};

export default TopBackground;
